import { Collapse, Input, Spin, Typography, message } from "antd";
import Axios from "axios";
import React, { useEffect, useMemo, useState } from "react";

const { Panel } = Collapse;

const tableCellStyle: React.CSSProperties = {
    border: "1px solid #d9d9d9",
    padding: 8,
    verticalAlign: "middle",
};

const tableHeaderStyle: React.CSSProperties = {
    ...tableCellStyle,
    background: "#fafafa",
    fontWeight: 600,
    textAlign: "left",
    whiteSpace: "nowrap",
};

export interface MsLine {
    key: string;
    name: string;
}

interface EntrepriseMsData {
    siret: string,
  raison_sociale: string,
  address: string,
  postal_code: string,
  commune: string
}

function StoreRow({ line }: { line: EntrepriseMsData }) {
    return (
        <tr>
            <td style={tableCellStyle}>{line.siret ?? ""}</td>
            <td style={tableCellStyle}>{line.raison_sociale}</td>
            <td style={tableCellStyle}>{line.address ?? ""}</td>
            <td style={tableCellStyle}>{line.postal_code ?? ""}</td>
            <td style={tableCellStyle}>{line.commune ?? ""}</td>
        </tr>
    );
}

function BotStoresTable({ msLine }: { msLine: MsLine }) {
    const [lines, setLines] = useState<EntrepriseMsData[] | null>(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let cancelled = false;
        setLoading(true);

        const loadEntrepriseMsData = async () => {
            try {
                const response = await Axios.get(`/ms/enseignesData/${msLine.name}`);
                if (cancelled) {
                    return;
                }
                setLines(response.data);
            } catch (error) {
                if (cancelled) {
                    return;
                }
                console.error("Erreur lors du chargement des magasins", error);
                message.error(`Erreur lors du chargement des données pour ${msLine.name}`);
                setLines([]);
            } finally {
                if (!cancelled) {
                    setLoading(false);
                }
            }
        };

        loadEntrepriseMsData();

        return () => {
            cancelled = true;
        };
    }, [msLine.name]);

    if (loading || lines === null) {
        return (
            <div style={{ textAlign: "center", padding: 24 }}>
                <Spin tip="Chargement des magasins" />
            </div>
        );
    }

    if (lines.length === 0) {
        return <Typography.Text type="secondary">Aucun magasin en production</Typography.Text>;
    }

    return (
        <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                    <tr>
                        <th style={tableHeaderStyle}>Siret</th>
                        <th style={tableHeaderStyle}>Nom</th>
                        <th style={tableHeaderStyle}>Rue</th>
                        <th style={tableHeaderStyle}>CP</th>
                        <th style={tableHeaderStyle}>Commune</th>
                    </tr>
                </thead>
                <tbody>
                    {lines.map((line) => (
                        <StoreRow key={line.siret} line={line} />
                    ))}
                </tbody>
            </table>
        </div>
    );
}

export function Visualisation() {
    const [msLines, setMsLines] = useState<MsLine[] | null>(null);
    const [search, setSearch] = useState("");
    const [activeKeys, setActiveKeys] = useState<string[]>([]);

    useEffect(() => {
        console.log("Chargement des robots");
        console.log(new Date().toISOString());
        Axios.get("/ms/enseignes")
            .then((response) => {
                const enseignes: string[] = response.data;
                const uniqueMsLines = new Map<string, MsLine>();
                enseignes.forEach((enseigne) => {
                    const key = `${enseigne}`;
                    if (!uniqueMsLines.has(key)) {
                        uniqueMsLines.set(key, {
                            key,
                            name: enseigne,
                        });
                    }
                });
                setMsLines(Array.from(uniqueMsLines.values()));
            })
            .catch((error) => {
                console.error("Erreur lors du chargement des robots", error);
                message.error("Erreur lors du chargement des robots");
            });
    }, []);

    const filteredMsLines = useMemo(() => {
        if (!msLines) {
            return null;
        }
        const query = search.trim().toLowerCase();
        if (!query) {
            return msLines;
        }
        return msLines.filter((msLine) => msLine.name.toLowerCase().indexOf(query) !== -1);
    }, [msLines, search]);

    if (msLines === null) {
        return (
            <div style={{ textAlign: "center", marginTop: 50 }}>
                <Spin size="large" tip="Chargement" />
            </div>
        );
    }

    return (
        <div style={{ display: "flex", flexDirection: "column", gap: 12, height: "calc(100vh - 220px)", minHeight: 400 }}>
            <Input.Search
                autoFocus={true}
                placeholder="Rechercher un robot par nom"
                allowClear
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                style={{ maxWidth: 420 }}
            />
            <div style={{ flex: 1, overflowY: "auto", paddingRight: 8 }}>
                {filteredMsLines && filteredMsLines.length === 0 ? (
                    <Typography.Text type="secondary">Aucun robot trouvé</Typography.Text>
                ) : (
                    <Collapse
                        activeKey={activeKeys}
                        onChange={(keys) => setActiveKeys(Array.isArray(keys) ? keys : [keys])}
                    >
                        {filteredMsLines?.map((msLine) => (
                            <Panel header={msLine.name} key={msLine.key}>
                                {activeKeys.some((key) => key === msLine.key) ? <BotStoresTable msLine={msLine} /> : null}
                            </Panel>
                        ))}
                    </Collapse>
                )}
            </div>
        </div>
    );
}
